import { useRouter } from 'next/router';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetWorkerProfile } from '@/modules/workforce/Crew/apiHooks';
import { AddEditWorker } from '@/modules/workforce/Crew/components/Modals/AddEditWorker';
import { DeactivateWorkerConfirmation } from '@/modules/workforce/Crew/components/Modals/DeactivateWorkerConfirmation';
import { ContactInfo } from '@/modules/workforce/Crew/components/Workers/WorkerDetails/ContactInfo';
import { HeaderCards } from '@/modules/workforce/Crew/components/Workers/WorkerDetails/HeaderCards';
import { IdentityInfo } from '@/modules/workforce/Crew/components/Workers/WorkerDetails/IdentityInfo';
import { IAddWorkerForm } from '@/modules/workforce/Crew/types';
import { Button, Typography } from '@/shared/components/atoms';
import DemoCTA from '@/shared/components/atoms/Demo/Modals/DemoCTA';
import { BaseIcon } from '@/shared/components/atoms/IconButton/BaseIcon';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import DotedMenu from '@/shared/components/molecules/DotedMenu';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Edit from '~/images/shared/edit.svg';
import DotsMenu from '~/images/shared/HorizontalHollowDots.svg';
import Trash from '~/images/shared/TrashWithoutColor.svg';

export function WorkerProfile() {
  const { t } = useTranslation('common');
  const { query, replace, asPath } = useRouter();
  const [isDemoFarm] = useStore((store) => store.isDemoFarm);
  const { openModal, closeModal } = useModal();
  const { workerId, id, isEditing } = query;

  /************************************* api ************************************ */
  const { data } = useGetWorkerProfile(workerId as string);

  const handleEdit = () => {
    openModal(
      <AddEditWorker
        supplierId={data?.crew.supplierId ?? Number(id)}
        supplierName={data?.crew.supplierName ?? ''}
        workerId={Number(workerId)}
        initialValues={
          {
            name: data?.basicInfo.workerName,
            phoneNumber: data?.contactInfo.phoneNumber,
            nationalIdBack: data?.identityInfo.nationalIdBack
              ? [{ dataURL: data?.identityInfo.nationalIdBack }]
              : undefined,
            nationalIdFront: data?.identityInfo.nationalIdFront
              ? [
                  {
                    dataURL: data?.identityInfo.nationalIdFront,
                  },
                ]
              : undefined,
            profilePicture: data?.basicInfo.profilePicture
              ? [{ dataURL: data?.basicInfo.profilePicture }]
              : undefined,
            nationalIdNumbr: data?.identityInfo.nationalIdNumber,
            customId: data?.internalInfo.customId?.toString(),
            countryCode: data?.contactInfo.countryCode,
            nationalIdNumber: data?.identityInfo.nationalIdNumber,
          } as IAddWorkerForm
        }
      />,
      {
        variant: 'full',
        title: t('edit-worker'),
        titleVariant: 'h6',
        onClose: () =>
          openModal(
            <ConfirmationModal
              header={t('going-somewhere-else')}
              message={t('you-will-lose-all-data')}
              variant='warning'
              dismissText={t('continue-editing')}
              confirmText={t('discard-and-exit')}
              onDismissRemoveNumber={1}
              onConfirm={closeModal}
              buttonsAlignment='col'
              preferredAction='dismiss'
            />
          ),
      }
    );
  };

  useEffect(() => {
    if (isEditing && data) {
      const newRoute = asPath.split('?')[0];
      replace(newRoute);

      handleEdit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isEditing, asPath]);

  return (
    <section>
      <div className='flex flex-col gap-16'>
        <div className='flex w-full items-center justify-between'>
          <Typography variant='h2' weight='bold' className='capitalize'>
            {t('worker-details')}
          </Typography>
          <div className='flex w-fit items-center gap-16'>
            {data?.basicInfo.type === 'regular' &&
              data.basicInfo.status === 'ACTIVE' && (
                <Button
                  onClick={handleEdit}
                  variant='text'
                  size='none'
                  className='!rounded-xl border-xs border-outline-default'
                >
                  <span className='flex items-center gap-2 p-16 text-base font-medium'>
                    <BaseIcon size='sm'>
                      <Edit />
                    </BaseIcon>
                    {t('edit')}
                  </span>
                </Button>
              )}
            <DotedMenu
              list={[
                ...(data?.basicInfo.status === 'ACTIVE'
                  ? [
                      {
                        id: '0',
                        title: (
                          <div className='flex items-center gap-2 text-error-400'>
                            <Trash width='24' height='24' />
                            <span>{t('deactivate')}</span>
                          </div>
                        ),
                        action: () =>
                          openModal(
                            isDemoFarm ? (
                              <DemoCTA />
                            ) : (
                              <DeactivateWorkerConfirmation
                                workerId={Number(workerId)}
                                crewId={data?.crew.supplierId ?? Number(id)}
                                workerName={data?.basicInfo.workerName ?? ''}
                              />
                            ),
                            {
                              containerClassName: 'max-w-md',
                            }
                          ),
                      },
                    ]
                  : []),
              ]}
              getTitle={(item) => item.title}
              getId={(item) => item?.id}
              getAction={(item) => item.action}
              onOpen={() => null}
              DotsMenuIcon={
                <DotsMenu className='text-24 text-primary-default' />
              }
            />
          </div>
        </div>
        <HeaderCards
          basicInfo={data?.basicInfo}
          crew={data?.crew}
          qrCode={data?.qrCode}
          skills={data?.skills}
        />
        <IdentityInfo identityInfo={data?.identityInfo} />
        <ContactInfo
          contactInfo={data?.contactInfo}
          internalInfo={data?.internalInfo}
        />
      </div>
    </section>
  );
}
